import { useEffect, useState, useMemo } from "react";
import { getApiUrl } from '../../utils/getApiUrl';
import { useNavigate } from "react-router-dom";
import { useSelectedDictionary } from '../../context/selectedContext';
import { useLanguage } from '../../context/languageContext';
import type { DictionaryModel, ShowAllRowModel } from '../../type/dictionary';
import type { UserModel } from "../../type/user";
import { createDictionary as createDictionaryApi, updateDictionary as updateDictionaryApi, deleteDictionary as deleteDictionaryApi } from './dictionaryActions';
import { clearAuth, fetchUser, fetchWithAuth, isAuthenticated } from "../../auth/auth";
import { useSpinner } from "../../components/spinner/spinner";
import { useToast } from "../../components/toast/toast";
import PageHeader from "../../components/pageHeader/pageHeader"; 
import EmptyState from "../../components/emptyState/emptyState";
import AddDictionaryModal from "../../modal/addDictionary/addDictionaryModal";
import DictionaryRow from "./dictionaryRow";
import "./dictionaries.scss";
//import { useUserParameters } from "../../context/userParameters/userParametersContext";


const Dictionaries = () => {
  const [dictionaries, setDictionaries] = useState<DictionaryModel[]>([]);
  const [error, setError] = useState(false);
  const { setSelected } = useSelectedDictionary();
  const { selectedLanguageId } = useLanguage();
  const [user, setUser] = useState<UserModel | null>(null); 
  const { showSpinner, hideSpinner } = useSpinner();
  const { showToast } = useToast();
  const nav = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);
  //const { parameters } = useUserParameters();
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  //const { setParameters } = useUserParameters();
  
  useEffect(() => {
    if (isAuthenticated()) {
      fetchUser().then((userData) => {
        if (userData) {
          setUser(userData);
          //setParameters(userData?.parameters ?? {}); 
        }
      });

      // const userData = getUser();
      // setUser(userData);
    }
  }, []);

  useEffect(() => {
    if (user && selectedLanguageId) {
      loadDictionaries();
    }
  }, [user, selectedLanguageId]);

  useEffect(() => {
    setSelected(null);
  }, [setSelected]);

  const loadDictionaries = async () => {
    if (!user || !selectedLanguageId) return;

    try {
      setIsLoaded(false);
      showSpinner();

      // Transient 403/network errors can happen while the backend/DB connection pool
      // is still warming up right after the app starts. Retry once before failing.
      let res: Response;
      try {
        res = await fetchWithAuth(getApiUrl(`dictionaries?idlanguage=${selectedLanguageId}`), {
          headers: { "Content-Type": "application/json" },
        });
        if (!res.ok && res.status !== 401) {
          await new Promise((r) => setTimeout(r, 800));
          res = await fetchWithAuth(getApiUrl(`dictionaries?idlanguage=${selectedLanguageId}`), {
            headers: { "Content-Type": "application/json" },
          });
        }
      } catch {
        await new Promise((r) => setTimeout(r, 800));
        res = await fetchWithAuth(getApiUrl(`dictionaries?idlanguage=${selectedLanguageId}`), {
          headers: { "Content-Type": "application/json" },
        });
      }

      if (!res.ok) throw new Error("Failed to fetch");

      const data: DictionaryModel[] = await res.json();
      const filtered = data.filter((d) => !d.deleted);

      setDictionaries(filtered);
      setError(false);

      localStorage.setItem("app_dictionaries", JSON.stringify(filtered));
      document.dispatchEvent(
        new CustomEvent("dictionaries:updated", { detail: filtered })
      );
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      hideSpinner();
      setIsLoaded(true);
    }
  };

  const remainingDictionaries = () => {
    if (!user) return 0;
    if (user.max_dictionaries === -1) return Infinity;
    return Math.max((user?.max_dictionaries ?? 0) - dictionaries.length, 0);
  };
  const canCreateDictionary = () => remainingDictionaries() > 0;  

  const handleSaveDictionary = async (name: string) => {
    if (!user) return showToast("User not found. Please log in again.", "info");
    if (!selectedLanguageId) return showToast("Language not loaded yet. Please try again.", "info");

    try {
      await createDictionaryApi(user!.iduser!, name, selectedLanguageId, showSpinner, hideSpinner);
      await loadDictionaries();
    } catch (err) {
      console.error(err);
      showToast(err instanceof Error ? err.message : "Error creating dictionary.", "error");
    }
  };

  const updateDictionary = async (id: number, newNameValue: string) => {
    try {
      await updateDictionaryApi(id, newNameValue, showSpinner, hideSpinner);
      await loadDictionaries();
    } catch (err) {
      console.error(err);
      showToast(err instanceof Error ? err.message : "Error updating dictionary.", "error");
    }
  };

  const deleteDictionary = async (id: number) => {
    try {
      await deleteDictionaryApi(id, showSpinner, hideSpinner);
      await loadDictionaries();
    } catch (err) {
      console.error(err);
      showToast("Error deleting dictionary.", "error");
    }
  };

  const handleLogout = () => {
    clearAuth();
    nav("/login");
  };

  const filteredDictionaries = useMemo(() => {
    if (!searchQuery.trim()) return dictionaries;
    const query = searchQuery.toLowerCase().trim();
    return dictionaries.filter((d) =>
      d.name.toLowerCase().includes(query)
    );
  }, [dictionaries, searchQuery]);


  return (
    <div className="dictionaries-container">

      <PageHeader
        title="Dictionaries"
        icon="bi-collection"
        addButton={
          isLoaded && !error && user?.parameters?.addnewdictiionary_visible?.value
            ? {
                label: "New Dictionary",
                onClick: () => setShowModal(true),
                disabled: !canCreateDictionary(),
                //title: canCreateDictionary() ? "Create new dictionary" : "Dictionary limit reached",
                title: canCreateDictionary() 
                  ? "Create new dictionary" 
                  : `Dictionary limit reached (${dictionaries.length}/${user?.max_dictionaries} used)`,

              }
            : undefined
        }
        search={
          isLoaded && !error && dictionaries.length > 0
            ? {
                value: searchQuery,
                onChange: setSearchQuery,
                placeholder: "Search dictionaries...",
              }
            : undefined
        }
      />

      {error && (
        <div className="dictionaries-error">
          <i className="bi bi-exclamation-triangle-fill dictionaries-error__icon" />
          <h4 className="dictionaries-error__title">Failed to load dictionaries</h4>
          <p className="dictionaries-error__text">
            Something went wrong. Try logging out and logging in again.
          </p>
          <button 
            className="btn btn-dark dictionaries-error__btn"
            onClick={handleLogout}
          >
            <i className="bi bi-box-arrow-right me-2" />
            Logout
          </button>
        </div>
      )}

      {isLoaded && !error && (
        <div className="dictionaries-list-container">
          <div className="dictionaries-list">
            <ShowAllRow dictionaries={dictionaries} />
            
            {filteredDictionaries.length === 0 && searchQuery.trim() && dictionaries.length > 0 && (
              <EmptyState
                icon="bi bi-search"
                title="No dictionaries found"
                description={`No dictionaries match "${searchQuery}"`}
                button={{
                  label: 'Clear search',
                  onClick: () => setSearchQuery(''),
                  variant: 'outline-primary'
                }}
                className="dictionaries-empty-search"
              />
            )}

            {filteredDictionaries.map((dict, index) => (
              <DictionaryRow
                key={dict.iddictionary}
                dict={dict}
                onUpdate={updateDictionary}
                onDelete={deleteDictionary}
                showToast={showToast}
                parameters={user?.parameters}
                index={index}
                user={user}
              />
            ))}
          </div>
        </div>
      )}

      {isLoaded && !error && dictionaries.length === 0 && (
        <EmptyState
          icon="bi bi-journal-x"
          title="No dictionaries yet"
          description="Create your first dictionary to get started!"
          button={
            canCreateDictionary() && user?.parameters?.addnewdictiionary_visible?.value
              ? {
                  label: 'New Dictionary',
                  onClick: () => setShowModal(true),
                  icon: 'bi bi-plus-lg'
                }
              : undefined
          }
          className="dictionaries-empty"
        />
      )}

      <AddDictionaryModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSave={handleSaveDictionary}
        canAddDictionary={canCreateDictionary()}
        remainingDictionaries={remainingDictionaries()}
        maxDictionaries={user?.max_dictionaries ?? 0}
        packageName={user?.package_name}
      />
    </div>
  );
};


const ShowAllRow: React.FC<ShowAllRowModel> = ({ dictionaries }) => {
  const navigate = useNavigate();
  const { setSelected } = useSelectedDictionary();

  const openAll = () => {
    setSelected({ id: undefined, name: "All Dictionaries" });
    navigate("/words");
  };

  return (
    <div
      onClick={openAll}
      className="dictionaries-item dictionaries-item--all"
      role="listitem"
    >
      <div className="dictionaries-item__icon dictionaries-item__icon--all">
        <i className="bi bi-collection" />
      </div>
      <div className="dictionaries-item__content">
        <span className="dictionaries-item__name dictionaries-item__name--all">
          Show All Expressions
        </span>
        <span className="dictionaries-item__count">
          {dictionaries.length} {dictionaries.length === 1 ? "dictionary" : "dictionaries"}
        </span>
      </div>
      <div className="dictionaries-item__arrow">
        <i className="bi bi-chevron-right" />
      </div>
    </div>
  );
};

export default Dictionaries;